import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';

// Import all components
import VinInputSection from './components/VinInputSection';
import VehicleIdentificationCard from './components/VehicleIdentificationCard';
import VersionSelector from './components/VersionSelector';
import EquipmentAccordion from './components/EquipmentAccordion';
import CommercialValuesCard from './components/CommercialValuesCard';
import ExportModal from './components/ExportModal';
import LoadingSkeleton from './components/LoadingSkeleton';

// Import services
import { fetchVinData, validateVin } from '../../services/api';
import { addQueryRecord, getSession, getTodayQueries } from '../../services/localStorage';
import {
  mapCesviToVehicle,
  mapCesviToVersion,
  mapCesviToEquipment,
  mapCesviToCommercial,
  createQueryRecord
} from '../../services/dataMappers';
import { getDeviceInfo } from '../../utils/deviceInfo';

const VinConsultation = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [vehicleData, setVehicleData] = useState(null);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [equipmentData, setEquipmentData] = useState(null);
  const [commercialData, setCommercialData] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);

  // Get remaining queries for today
  const todayQueries = getTodayQueries();
  const remainingQueries = Math.max(0, 50 - todayQueries.length);

  const handleVinSubmit = async (vin) => {
    setIsLoading(true);
    setError('');
    setVehicleData(null);
    setAvailableVersions([]);
    setSelectedVersion(null);
    setEquipmentData(null);
    setCommercialData(null);
    setApiResponse(null);

    const startTime = performance.now();
    const session = getSession();
    const deviceInfo = getDeviceInfo();

    try {
      // Validate VIN format
      const validation = validateVin(vin);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      const cleanVin = validation.cleanVin;

      // Call CESVI API
      const response = await fetchVinData(cleanVin);
      const latency = performance.now() - startTime;

      // Store raw response for reference
      setApiResponse(response);

      // Handle API response
      if (!response.success || !response.versions?.length) {
        throw new Error('No se encontraron datos para este VIN');
      }

      // Map data to UI format using the first version
      const firstVersion = response.versions[0];
      const mappedVehicle = mapCesviToVehicle(firstVersion, cleanVin);
      const mappedVersions = response.versions.map(mapCesviToVersion);
      const mappedEquipment = mapCesviToEquipment(firstVersion);
      const mappedCommercial = mapCesviToCommercial(firstVersion);

      // Update state with mapped data
      setVehicleData(mappedVehicle);
      setAvailableVersions(mappedVersions);
      setSelectedVersion(mappedVersions[0]);
      setEquipmentData(mappedEquipment);
      setCommercialData(mappedCommercial);

      // Save successful query to localStorage
      const queryRecord = createQueryRecord({
        vin: cleanVin,
        vehicle: mappedVehicle,
        status: 'success',
        latency,
        versionsCount: response.totalVersions,
        commercialValue: firstVersion.precioLista || 0,
        user: session ? {
          email: session.email,
          name: session.userName,
          role: session.role
        } : { email: 'anonymous', name: 'Anónimo', role: 'guest' },
        deviceInfo
      });
      addQueryRecord(queryRecord);

    } catch (err) {
      const latency = performance.now() - startTime;
      setError(err?.message || 'Error al consultar el VIN');

      // Save failed query to localStorage
      const queryRecord = createQueryRecord({
        vin: vin,
        vehicle: null,
        status: err.message?.includes('inválido') || err.message?.includes('caracteres') ? 'invalid' : 'error',
        latency,
        versionsCount: 0,
        commercialValue: 0,
        user: session ? {
          email: session.email,
          name: session.userName,
          role: session.role
        } : { email: 'anonymous', name: 'Anónimo', role: 'guest' },
        deviceInfo
      });
      addQueryRecord(queryRecord);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVersionSelect = (version) => {
    setSelectedVersion(version);

    // If version has original CESVI data, update equipment and commercial data
    if (version?._original) {
      const mappedEquipment = mapCesviToEquipment(version._original);
      const mappedCommercial = mapCesviToCommercial(version._original);
      setEquipmentData(mappedEquipment);
      setCommercialData(mappedCommercial);
    }
  };

  const handleExport = (exportData) => {
    // Simulate file download
    const filename = `vin-${vehicleData?.vin?.toLowerCase()}-${new Date()?.toISOString()?.split('T')?.[0]}.${exportData?.format}`;

    // Create download link
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopyJson = async () => {
    if (!vehicleData) return;

    const jsonData = {
      vehicle: vehicleData,
      version: selectedVersion,
      equipment: equipmentData,
      commercial: commercialData,
      timestamp: new Date()?.toISOString()
    };

    try {
      await navigator.clipboard?.writeText(JSON.stringify(jsonData, null, 2));
      setJsonCopied(true);
      setTimeout(() => setJsonCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy JSON:', err);
    }
  };

  const handleViewHistory = () => {
    navigate('/history-and-audit-trail');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-60'}`}>
        <div className="p-4">
          <Breadcrumbs />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Consulta de VIN</h1>
              <p className="text-muted-foreground mt-1">
                Decodificación completa de vehículos mediante número VIN
              </p>
            </div>
            
            {vehicleData &&
            <div className="flex items-center space-x-2">
                <Button
                variant="outline"
                size="sm"
                onClick={handleCopyJson}
                iconName={jsonCopied ? "Check" : "Copy"}
                iconPosition="left"
                iconSize={14}
                className={jsonCopied ? "text-success border-success" : ""}>

                  {jsonCopied ? 'Copiado' : 'Copiar JSON'}
                </Button>
                <Button
                variant="outline"
                size="sm"
                onClick={() => setIsExportModalOpen(true)}
                iconName="Download"
                iconPosition="left"
                iconSize={14}>

                  Exportar
                </Button>
                <Button
                variant="default"
                size="sm"
                onClick={handleViewHistory}
                iconName="History"
                iconPosition="left"
                iconSize={14}>

                  Ver Historial
                </Button>
              </div>
            }
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* VIN Input Section */}
            <VinInputSection
              onVinSubmit={handleVinSubmit}
              isLoading={isLoading}
              error={error} />


            {/* Loading State */}
            {isLoading &&
            <div className="space-y-4">
                <LoadingSkeleton type="identification" />
                <LoadingSkeleton type="equipment" />
                <LoadingSkeleton type="commercial" />
              </div>
            }

            {/* Results */}
            {vehicleData && !isLoading &&
            <>
                {/* Vehicle Identification */}
                <VehicleIdentificationCard vehicleData={vehicleData} />

                {/* Version Selection */}
                {availableVersions?.length > 1 &&
              <VersionSelector
                versions={availableVersions}
                selectedVersion={selectedVersion}
                onVersionSelect={handleVersionSelect} />

              }

                {/* Equipment Details */}
                <EquipmentAccordion equipmentData={equipmentData} />

                {/* Commercial Values */}
                <CommercialValuesCard commercialData={commercialData} />
              </>
            }

            {/* Empty State */}
            {!vehicleData && !isLoading &&
            <div className="text-center py-8">
                <div className="flex items-center justify-center w-12 h-12 bg-muted rounded-full mx-auto mb-3">
                  <Icon name="Search" size={20} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  Ingrese un VIN para comenzar
                </h3>
                <p className="text-muted-foreground mb-4 max-w-md mx-auto text-sm">
                  Introduzca un número VIN válido de 17 caracteres para obtener información completa del vehículo, equipamiento y valores comerciales.
                </p>
                <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={12} />
                    <span>Consultas restantes: {remainingQueries}/50</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Shield" size={12} />
                    <span>Datos CESVI México</span>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </main>
      {/* Export Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        vehicleData={vehicleData}
        onExport={handleExport} />

      <QuickActionButton />
    </div>);

};

export default VinConsultation;